import React, { useState } from "react"
import { Form, Input, Button, message as antdMessage, Spin } from "antd"
import { useNavigate, useSearchParams } from "react-router-dom"
import { authApi } from "../../Apis/authApis"
import usePasswordValidation from "../../utils/PasswordCheck"
import { translations } from "../../utils/Translation/staticTranslation"
import ToggleLogin from "../../components/Common/ToggleLogin"
import useLanguage from "../../services/useLanguage"

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const uidb64 = searchParams.get("uidb64")
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const { passwordChecked, validatePassword } = usePasswordValidation()
  const { language, handleLanguageChange } = useLanguage()

  const lang = localStorage.getItem("language") || "ar"
  const reset_password = translations[lang]?.reset_password
  const loginPage = translations[lang]?.loginPage

  const onFinish = async (values) => {
    if (!passwordChecked.lengthCheck || !passwordChecked.otherChecks) {
      return antdMessage.error(reset_password?.requirement_not_match_msg)
    }

    try {
      setLoading(true)
      const payload = {
        password: values.password,
        confirm_password: values.confirmPassword,
        token,
        uidb64
      }

      const response = await authApi.resetPassword(payload)
      if (response.data.success) {
        antdMessage.success(
          `${
            lang === "en" ? response.data.message : response.data.messageArabic
          }`
        )
        navigate("/login")
        form.resetFields()
      }
    } catch (error) {
      const message =
        lang === "en"
          ? error.response?.data?.detail
          : error.response?.data?.detailArabic

      antdMessage.error(message)
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="signup-inner">
        <div className="signup-left">
          <div className="signup-left-logo">
            <img src="/signup/Zory brown.svg" alt="Zory Logo" />
          </div>
        </div>

        <div className="signup-right">
          <div className="sign-l-header" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="back-arrow-reset"></div>
            <div className="logo-sign">
              <img src="/signup/Zorysmall.svg" alt="Zory Logo" />
            </div>
            <div className="lang-toggle">
              <ToggleLogin
                toggleLanguage={handleLanguageChange}
                currentLanguage={language}
              />
            </div>
          </div>

          <div className="content">
            <h3 className="font-Montserrat00">
              {reset_password?.reset_password_title}
            </h3>
            <span className="font-Montserrat-300">
              {reset_password?.reset_password_description}
            </span>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ marginTop: "24px" }}
          >
            <Form.Item
              label={
                <span style={lang === "ar" ? { fontSize: "13px" } : undefined}>
                  {reset_password?.password}
                </span>
              }
              name="password"
              rules={[
                {
                  required: true,
                  message: reset_password?.password_error,
                },
              ]}
              labelCol={{ span: 24 }}
            >
              <Input.Password
                className="custom-input"
                placeholder={reset_password?.password}
                onChange={(e) => validatePassword(e.target.value)}
                style={{
                  background: "#ffffff",
                  border: "1px solid rgba(132, 135, 136, 0.25)",
                  height: 50,
                  borderRadius: 12,
                }}
              />
            </Form.Item>

            {/* Password validation indicators */}
            <div style={{ marginBottom: "16px", marginTop: "-8px" }}>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  fontSize: "12px",
                }}
              >
                <li
                  style={{
                    opacity: passwordChecked.otherChecks ? 1 : 0.5,
                    color: passwordChecked.otherChecks ? "#52c41a" : "#8c8c8c",
                    marginBottom: "4px",
                  }}
                >
                  • {reset_password?.validation_1}
                </li>
                <li
                  style={{
                    opacity: passwordChecked.lengthCheck ? 1 : 0.5,
                    color: passwordChecked.lengthCheck ? "#52c41a" : "#8c8c8c",
                  }}
                >
                  • {reset_password?.validation_2}
                </li>
              </ul>
            </div>

            <Form.Item
              label={
                <span style={lang === "ar" ? { fontSize: "13px" } : undefined}>
                  {reset_password?.confirm_password}
                </span>
              }
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                {
                  required: true,
                  message: reset_password?.confirm_password_error,
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error(reset_password?.passwordMatch)
                    )
                  },
                }),
              ]}
              labelCol={{ span: 24 }}
            >
              <Input.Password
                className="custom-input"
                placeholder={reset_password?.confirm_password}
                style={{
                  background: "#ffffff",
                  border: "1px solid rgba(132, 135, 136, 0.25)",
                  height: 50,
                  borderRadius: 12,
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: "100%",
                  fontSize: "18px",
                  height: 50,
                  borderRadius: 12,
                  background: "#1d869a",
                  border: "none",
                }}
              >
                {loading ? <Spin /> : reset_password?.reset}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  )
}

export default ResetPassword
